/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { compose } from 'recompose';

import redirect from 'js/lib/coursera.redirect';
import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import { useTracker } from '@coursera/event-pulse/react';

import withCourseraPlusProductEligibilityCheck from 'bundles/coursera-plus-common/utils/withCourseraPlusProductEligibilityCheck';
import type { EligibilityCheckProps } from 'bundles/coursera-plus-common/utils/withCourseraPlusProductEligibilityCheck';
import type { PropsFromCourseraPlusProductOwnerships } from 'bundles/coursera-plus-common/utils/withCourseraPlusProductOwnerships';
import withCourseraPlusProductOwnerships from 'bundles/coursera-plus-common/utils/withCourseraPlusProductOwnerships';
import QueryProductOwnershipsById from 'bundles/enroll-common/api/QueryProductOwnershipsById.graphql';
import type {
  QueryProductOwnershipsById_Dedupe1Query as QueryProductOwnershipsByIdQuery,
  QueryProductOwnershipsById_Dedupe1QueryVariables as QueryProductOwnershipsByIdQueryVariables,
} from 'bundles/enroll-common/api/__generated__/QueryProductOwnershipsById';
import EnrollmentChoiceTypes from 'bundles/enroll-common/common/EnrollmentChoiceTypes';
import FinaidDeterrentModal from 'bundles/enroll-common/components/finaid/FinaidDeterrentModal';
import { getPreEnrollmentFlowVariant } from 'bundles/enroll-common/components/xdp/enrollSectionUtils';
import withEnrollment from 'bundles/enroll-common/components/xdp/withEnrollment';
import type { PropsFromWithEnrollment } from 'bundles/enroll-common/components/xdp/withEnrollment';
import {
  choiceTypeToHandleSubmitPromise,
  submitEnrollmentPromise,
} from 'bundles/enroll-common/lib/enrollmentChoiceUtils';
import type { Product, ProductId } from 'bundles/enroll-common/types/productTypes';
import { hasCourse, hasCourseId, hasSpecialization } from 'bundles/enroll-common/types/productTypes';
import { useShouldShowFinaid } from 'bundles/enroll-common/utils/finaidUtils';
import EnrollErrorModal from 'bundles/enroll-modal/components/common/EnrollErrorModal';
import { formatGraphqlProductOwnership } from 'bundles/entitlements-common/utils/entitlementProductOwnershipUtils';
import GoogleCertificateEpicClient from 'bundles/epic/clients/GoogleCertificate';
import CourseChoiceModal from 'bundles/finaid-common/components/CourseChoiceModal';
import FinancialAidApplicationsLimitModal from 'bundles/finaid-common/components/FinancialAidApplicationsLimitModal';
import { MAX_PENDING_FINAID_APPLICATIONS } from 'bundles/finaid-common/constants/limits';
import type { FinancialAidApplicationType } from 'bundles/finaid-common/enhancers/types';
import {
  withFinancialAidApplicationsByUser,
  withFinancialAidApplicationsByUserAndProduct,
} from 'bundles/finaid-common/enhancers/withFinancialAidApplications';
import { isFinaidCeilingEnabled } from 'bundles/finaid-common/utils/financialAidCeilingUtils';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/enroll';

import 'css!./__styles__/FinaidLink';

export type PropsFromCaller = ProductId &
  Product & {
    applyTextComponent?: (link: React.ReactNode) => React.ReactNode;
    applyText?: string;
    alreadyAppliedText?: string;
    wrapperClass?: string;
    canOpenFinAidOnMount?: boolean;
    courseIds?: Array<string>;
    children?: React.ReactNode;
  };

type PropsFromEnrollmentProvider = PropsFromWithEnrollment &
  EligibilityCheckProps &
  PropsFromCourseraPlusProductOwnerships;

type PropsFromFinaidLinkS12nOwnershipProvider = {
  firstUnownedCourseId?: string;
};

type PropsFromWithFinaidApplications = {
  financialAidApplicationsByUserAndProduct?: FinancialAidApplicationType[];
  financialAidApplicationsByUser?: FinancialAidApplicationType[];
};

export type PropsToComponent = PropsFromCaller &
  PropsFromEnrollmentProvider &
  PropsFromFinaidLinkS12nOwnershipProvider &
  PropsFromWithFinaidApplications;

export const FinaidLink = (props: PropsToComponent) => {
  const {
    children,
    applyTextComponent,
    applyText = _t('Apply for Financial Aid'),
    alreadyAppliedText = _t(
      `You have already applied for financial aid.
            We will contact you if you are approved, or if we need more information from you.`
    ),
    wrapperClass = '',
    canOpenFinAidOnMount,
    firstUnownedCourseId,
    financialAidApplicationsByUserAndProduct: finaidApplicationsForCourse,
    financialAidApplicationsByUser: finaidApplicationsForUser,
    enrollmentAvailableChoices,
  } = props;

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showDeterrentModal, setShowDeterrentModal] = useState(false);
  const [showCourseChoiceModal, setShowCourseChoiceModal] = useState(false);
  const [showFinaidLimitModal, setShowFinaidLimitModal] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState<string | undefined>(undefined);

  const track = useTracker();
  const router = useRouter();

  const hasPendingCourseApplications = () => {
    return (
      finaidApplicationsForCourse?.some(
        (application) => application.state === 'PENDING' || application.state === 'PAYMENT_PENDING'
      ) || false
    );
  };

  const handleClick = (ev?: React.MouseEvent<HTMLElement>): void => {
    track('click_button', {
      button: { name: 'finaid_enroll' },
      pageSection: { sectionName: 'hero_banner' },
    });

    ev?.preventDefault();

    const { pathname, query } = router.location;

    if (!user.isAuthenticatedUser()) {
      router.replace({
        pathname,
        query: { ...query, ...{ authMode: 'login', aid: 'true' } },
      });
      return;
    }

    const pendingUserApplications = finaidApplicationsForUser?.filter((application) => application.state === 'PENDING');

    if (pendingUserApplications && pendingUserApplications.length >= MAX_PENDING_FINAID_APPLICATIONS) {
      setShowFinaidLimitModal(true);
    } else if (hasSpecialization(props)) {
      setShowCourseChoiceModal(true);
    } else {
      setShowDeterrentModal(true);
    }
  };

  useEffect(() => {
    const alreadyApplied = !hasSpecialization(props) && hasPendingCourseApplications();
    const openFinAid = Boolean(!alreadyApplied && router?.location?.query?.openFinAid === 'true');
    if (!hasSpecialization(props) && openFinAid && canOpenFinAidOnMount && !showDeterrentModal) {
      handleClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCourseChoiceSubmit = (courseId?: string) => {
    setSelectedCourseId(courseId);
    setShowCourseChoiceModal(false);
    setShowDeterrentModal(true);
  };

  const renderFinaidPrompt = () => {
    const alreadyApplied = !hasSpecialization(props) && hasPendingCourseApplications();

    if (children) {
      return children;
    } else if (alreadyApplied) {
      return alreadyAppliedText;
    } else {
      const link = (
        <TrackedButton
          data-testid="finaid-link"
          className="button-link finaid-link"
          onClick={handleClick}
          trackingName="finaid"
        >
          {applyText}
        </TrackedButton>
      );

      return applyTextComponent ? applyTextComponent(link) : link;
    }
  };

  const renderFinAidErrorModal = () => {
    const courseId = hasCourse(props) ? props.course.id : undefined;
    const s12nId = hasSpecialization(props) ? props.s12n.id : undefined;
    return (
      <EnrollErrorModal onClose={() => setShowErrorModal(false)} isFinancialAid courseId={courseId} s12nId={s12nId} />
    );
  };

  const redirectToFinancialAidApplication = (productType: string, productId: string) => {
    redirect.setLocation(`/payments/financial-aid/${productType}/${productId}`);
  };

  const redirectToFinancialAidLegacayApplication = () => {
    const finaidCourseId = hasSpecialization(props) ? selectedCourseId || firstUnownedCourseId : props.course.id;
    const additionalQueryParams = {
      ...(hasSpecialization(props) ? { specialization: props.s12n.slug } : {}),
      ...(hasCourse(props) ? { course: props.course.slug } : {}),
      ...(finaidCourseId ? { courseId: finaidCourseId } : {}),
    };
    const handleSubmitPromise = choiceTypeToHandleSubmitPromise[EnrollmentChoiceTypes.PURCHASE_SINGLE_COURSE];
    const promiseRequest = {
      courseId: finaidCourseId,
      ...(hasSpecialization(props) ? { data: { s12nId: props.s12n.id } } : {}),
    };

    submitEnrollmentPromise({
      handleSubmitPromise,
      options: promiseRequest,
      additionalParams: additionalQueryParams,
      isFinaid: true,
    }).catch(() => {
      setShowErrorModal(true);
    });
  };

  const renderFinAidDeterrentModal = () => {
    const finaidCourseId = hasSpecialization(props) ? selectedCourseId || firstUnownedCourseId : props.course.id;
    const routeToFinancialAidApplicationV3 = GoogleCertificateEpicClient.preview('routeToFinancialAidApplicationV3');
    const isFinancialAidCeilingVariant = isFinaidCeilingEnabled();
    if (!finaidCourseId) return null;

    return (
      <FinaidDeterrentModal
        courseId={finaidCourseId}
        productType={hasSpecialization(props) && props.s12n.id && !selectedCourseId ? 'specialization' : 'course'}
        s12nId={hasSpecialization(props) ? props.s12n.id : undefined}
        onContinue={() => {
          setShowDeterrentModal(false);

          if (routeToFinancialAidApplicationV3 || isFinancialAidCeilingVariant) {
            redirectToFinancialAidApplication('course', finaidCourseId);
          } else {
            redirectToFinancialAidLegacayApplication();
          }
        }}
        onClose={() => setShowDeterrentModal(false)}
      />
    );
  };

  const renderFinAidLimitModal = () => {
    // sorted by approvalDate ascending
    const pendingUserApplications = finaidApplicationsForUser
      ?.filter((application) => application.state === 'PENDING')
      ?.sort((a, b) => (a?.approvalDate ?? 0) - (b?.approvalDate ?? 0));

    if (!pendingUserApplications?.length) return null;

    return (
      <FinancialAidApplicationsLimitModal
        // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        approvalDate={pendingUserApplications[0].approvalDate ?? null}
        applicationsCount={pendingUserApplications.length}
        applicationsMax={MAX_PENDING_FINAID_APPLICATIONS}
        handleClose={() => setShowFinaidLimitModal(false)}
      />
    );
  };

  const product = hasCourse(props)
    ? {
        course: props.course,
      }
    : {
        s12n: props.s12n,
      };

  const shouldShowFinaid = useShouldShowFinaid({ enrollmentAvailableChoices, ...product });

  // @ts-expect-error: next-line
  if (!shouldShowFinaid || (!props.course && !props.s12n)) {
    return null;
  }

  const wrapperClasses = classNames('rc-FinaidLink', wrapperClass);

  return (
    <div className={wrapperClasses} data-e2e="finaid-link">
      <p className="caption-text">{renderFinaidPrompt()}</p>

      {showErrorModal && renderFinAidErrorModal()}

      {showDeterrentModal && renderFinAidDeterrentModal()}
      {showCourseChoiceModal && hasSpecialization(props) && (
        <CourseChoiceModal
          s12nId={props.s12n.id}
          s12nSlug={props.s12n.slug}
          handleSubmit={handleCourseChoiceSubmit}
          handleClose={() => setShowCourseChoiceModal(false)}
        />
      )}
      {showFinaidLimitModal && renderFinAidLimitModal()}
    </div>
  );
};

const WithFinaidApplications = compose<PropsToComponent, PropsFromCaller & PropsFromFinaidLinkS12nOwnershipProvider>(
  // Only need to fetch the max pending application amount
  // to know if the learner already reached the limit (no need to fetch more than the max)
  withFinancialAidApplicationsByUser({ limit: MAX_PENDING_FINAID_APPLICATIONS }),
  withFinancialAidApplicationsByUserAndProduct(undefined, true)
)(FinaidLink);

const FinaidLinkS12nOwnershipProvider = (props: PropsFromCaller & PropsFromEnrollmentProvider) => {
  const userId = user.get().id?.toString();
  const courseIds = props.courseIds ?? [];
  const productOwnershipIds = courseIds.map((courseId) => `${userId}~VerifiedCertificate~${courseId}`);

  const { data } = useQuery<QueryProductOwnershipsByIdQuery, QueryProductOwnershipsByIdQueryVariables>(
    QueryProductOwnershipsById,
    {
      variables: { productOwnershipIds },
      context: { clientName: 'gatewayGql' },
      skip: !(userId && hasSpecialization(props) && productOwnershipIds.length > 0),
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
    }
  );

  const s12nCourseOwnerships = (data?.Entitlement?.queryProductOwnershipsById.edges ?? [])
    .map((edge) => edge && formatGraphqlProductOwnership(edge.node))
    .filter(Boolean);

  const firstUnownedCourseOwnership = s12nCourseOwnerships.find((ownership) => !ownership?.owns);
  let courseId;
  if (firstUnownedCourseOwnership?.productId) {
    courseId = firstUnownedCourseOwnership.productId;
  } else if (hasCourseId(props)) {
    courseId = props.courseId;
  }

  return (
    <WithFinaidApplications
      {...props}
      firstUnownedCourseId={firstUnownedCourseOwnership?.productId}
      {...(courseId ? { courseId } : {})}
    />
  );
};

export const shouldRenderFinaidLinkFromEnrollmentCheck = ({
  enrollmentAvailableChoices,
  ownsCourseraPlus,
  ownsCourseraLite,
  isCourseraPlusEligibleProduct,
}: PropsFromEnrollmentProvider) => {
  const preEnrollmentFlowVariant = getPreEnrollmentFlowVariant({
    enrollmentAvailableChoices,
  });

  // Surpress the financial aid if user is in the pre enrollment flow
  if (preEnrollmentFlowVariant && enrollmentAvailableChoices?.hasOnlyPreEnrollEnabledS12ns) {
    return false;
  }
  // Don't mention financial aid if user is subscribed to Plus or Lite, and viewing Plus content
  else if ((ownsCourseraPlus || ownsCourseraLite) && isCourseraPlusEligibleProduct) {
    return false;
  }

  return true;
};

const FinaidLinkWithEnrollmentCheck = (props: PropsFromCaller & PropsFromEnrollmentProvider) => {
  if (!shouldRenderFinaidLinkFromEnrollmentCheck(props)) {
    return undefined;
  }

  return <FinaidLinkS12nOwnershipProvider {...props} />;
};

export default compose<PropsFromCaller & PropsFromEnrollmentProvider, PropsFromCaller>(
  withEnrollment<PropsFromCaller>(true),
  withCourseraPlusProductEligibilityCheck(true),
  withCourseraPlusProductOwnerships(true)
)(FinaidLinkWithEnrollmentCheck);
